/**
 * Token utilities for handling authentication tokens
 * Separates PWA and browser sessions to prevent logout conflicts
 */

// Detect if running in standalone/PWA mode
const detectStandaloneMode = () => {
  // Standard standalone detection (Chrome, Edge Android PWA)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // iOS Safari standalone detection
  if (window.navigator.standalone === true) {
    return true;
  }
  
  // Fullscreen mode (some PWAs)
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true;
  }
  
  // Minimal UI mode
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true;
  }
  
  // TWA (Trusted Web Activity) detection
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
};

// Cache the standalone detection result (it won't change during the session)
const IS_PWA = detectStandaloneMode();

// Token key based on whether running as PWA or browser
const getTokenKey = () => {
  return IS_PWA ? 'fc_token_pwa' : 'fc_token_web';
};

// Legacy key for migration
const LEGACY_TOKEN_KEY = 'fc_token';

/**
 * Get the authentication token
 * Checks new key first, falls back to legacy for migration
 */
export const getToken = () => {
  const newKey = getTokenKey();
  let token = localStorage.getItem(newKey);
  
  // Migration: If no token in new key, check legacy and migrate
  if (!token) {
    const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
    if (legacyToken) {
      // Migrate to new key
      localStorage.setItem(newKey, legacyToken);
      // Remove legacy key to prevent issues
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      token = legacyToken;
      console.log('[TokenUtils] Migrated token from legacy key to:', newKey);
    }
  }
  
  return token;
};

/**
 * Set the authentication token
 */
export const setToken = (token) => {
  const key = getTokenKey();
  localStorage.setItem(key, token);
  console.log('[TokenUtils] Token saved to:', key, 'isPWA:', IS_PWA);
  
  // Also remove legacy key if exists to complete migration
  if (localStorage.getItem(LEGACY_TOKEN_KEY)) {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  }
};

/**
 * Remove the authentication token (logout)
 * Only removes token for current context (PWA or browser)
 */
export const removeToken = () => {
  const key = getTokenKey();
  localStorage.removeItem(key);
  console.log('[TokenUtils] Token removed from:', key, 'isPWA:', IS_PWA);
  
  // Also clean up related session data
  const prefix = IS_PWA ? 'fc_pwa_' : 'fc_web_';
  // Note: We leave the other context's token untouched
};

/**
 * Check if user has a valid token
 */
export const hasToken = () => {
  return !!getToken();
};

/**
 * Check if running as PWA
 */
export const isPWA = () => {
  return IS_PWA;
};

/**
 * Clear all tokens (for forced logout from all contexts)
 * Use sparingly - only for account deletion or security concerns
 */
export const clearAllTokens = () => {
  localStorage.removeItem('fc_token_pwa');
  localStorage.removeItem('fc_token_web');
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  console.log('[TokenUtils] All tokens cleared');
};

/**
 * Get user type from localStorage
 */
export const getUserType = () => {
  return localStorage.getItem('userType');
};

/**
 * Set user type in localStorage
 */
export const setUserType = (userType) => {
  localStorage.setItem('userType', userType);
};

/**
 * Remove notification-related storage items on logout
 */
export const clearSessionData = () => {
  localStorage.removeItem('fc_last_notification_time');
  localStorage.removeItem('fc_last_post_notification_time');
  localStorage.removeItem('myTabKey');

};

export default {
  getToken,
  setToken,
  removeToken,
  hasToken,
  isPWA,
  clearAllTokens,
  getUserType,
  setUserType,
  clearSessionData,
};
